import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import ReactDropzone from 'react-dropzone'
import { FormattedMessage } from 'react-intl'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCamera, faFilePdf, faTimes } from '@fortawesome/pro-regular-svg-icons'
import { faUpload } from '@fortawesome/free-solid-svg-icons'
import "./Dropzone.scss"

function Dropzone({ onDrop, onDelete, file, multiple, preview, allowImage, allowPdf, placeholder, validations }) {
    const [loading, setLoading] = useState(false)
    const [filePreview, setFilePreview] = useState(undefined)

    useEffect(() => {
        if (!file) {
            setFilePreview(undefined)
            return
        }
        if (typeof file === 'string') {
            setFilePreview(file)
            return
        }
        if (file instanceof Blob || file instanceof File) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setFilePreview(reader.result)
            };
            reader.readAsDataURL(file)
        }

    }, [file])

    const accept = useMemo(() => {
        const extensions = []
        if (allowImage) {
            extensions.push('.jpg')
            extensions.push('.jpeg')
            extensions.push('.png')
        }
        if (allowPdf) {
            extensions.push('.pdf')
        }
        return extensions.join(', ')
    }, [allowPdf])

    function handleDrop(files) {
        setLoading(true)
        const res = multiple ? files : files[0]
        onDrop && onDrop(res)
        setLoading(false)
    }

    const LoadingMessage = (
        <p className={'text-center'}>
            <br />
            <FormattedMessage id="wording.file.loading" defaultMessage="Chargement de votre fichier..." />
        </p>
    )

    const Labels = useMemo(() => {
        const mappedValidations = [...validations]
        let icon = faUpload
        if (mappedValidations.length === 0) {
            if (allowImage && allowPdf) {
                mappedValidations.push(<FormattedMessage id="wording.file.type_all" defaultMessage="JPEG, PNG ou PDF" />)
            } else if (allowImage) {
                mappedValidations.push(<FormattedMessage id="wording.file.type" defaultMessage="JPEG ou PNG" />)
                icon = faCamera
            } else if (allowPdf) {
                mappedValidations.push(<FormattedMessage id="wording.file.type_pdf" defaultMessage="PDF" />)
                icon = faFilePdf
            }
        }
        return (
            <>
                <FontAwesomeIcon icon={icon} className='icon' />
                <strong className="text-center label-head">
                    {placeholder}
                </strong>
                <span className="text-center">
                    {mappedValidations.map((validation, i) => (
                        <span key={i}>
                            {validation}
                            <br />
                        </span>
                    ))}
                    <FormattedMessage id="wording.file.size" defaultMessage="inférieurs à 10Mo" />
                </span>
            </>
        )
    }, [placeholder, validations, allowImage, allowPdf])

    const DeleteButton = useMemo(() => {
        if (!onDelete) {
            return null
        }
        return (
            <FontAwesomeIcon
                icon={faTimes}
                className={'delete-icon'}
                onClick={event => {
                    event.stopPropagation()
                    onDelete(event)
                }}
            />
        )
    }, [onDelete])

    const Preview = useMemo(() => {
        if (filePreview?.match(/\.pdf$/i) ||
            filePreview?.match(/^data:application\/pdf/i)) {
            return (
                <>
                    {DeleteButton}
                    <iframe className="preview-pdf" src={filePreview} />
                </>
            )
        }
        return (
            <>
                {DeleteButton}
                <img src={filePreview} alt="" />
            </>
        )
    }, [filePreview])

    return (
        <ReactDropzone onDrop={handleDrop} multiple={multiple} preview={preview} accept={accept}>
            {({ getRootProps, getInputProps }) => (
                <section className={'custom-file'} style={{}}>
                    <div {...getRootProps()}>
                        {loading
                            ? LoadingMessage
                            : <>
                                <input {...getInputProps()} />
                                <div className="custom-file-label">
                                    {(filePreview || file) ? Preview : Labels}
                                </div>
                            </>}
                    </div>
                </section>
            )}
        </ReactDropzone>
    )
}

Dropzone.defaultProps = {
    multiple: false,
    preview: true,
    allowImage: true,
    allowPdf: false,
    placeholder: (
        <FormattedMessage
            id="wording.file.button"
            defaultMessage="Cliquez ici pour {br} ajouter une image"
            values={{ br: <br /> }}
        />
    ),
    validations: []
}

Dropzone.propTypes = {
    onDrop: PropTypes.func,
    onDelete: PropTypes.func,
    file: PropTypes.any,
    multiple: PropTypes.bool,
    preview: PropTypes.bool,
    allowImage: PropTypes.bool,
    allowPdf: PropTypes.bool,
    placeholder: PropTypes.node,
    validations: PropTypes.arrayOf(PropTypes.node),
}

export default Dropzone;
